import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UsersRound, Search, GraduationCap, LayoutGrid, CalendarDays, Trophy, PencilLine, CalendarCheck2, Library, LayoutDashboard, BookText, ClipboardCheck, FileText, FolderOpen, Pencil, Trash2, ScanLine, FileCheck2 } from "lucide-react";
import { GRADE_GROUPS, GRADE_COLORS, EXAM_TRACK_GRADES } from "@/lib/subjects";
import { toast } from "sonner";

export default function CoachStudents() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [grade, setGrade] = useState("");
  const [q, setQ] = useState("");

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    const params = grade ? { grade: Number(grade) } : {};
    api.get("/students", { params }).then(({ data }) => setStudents(data));
  }, [grade]);

  const filtered = students.filter((s) => s.name.toLowerCase().includes(q.toLowerCase()));

  const delStudent = async (s) => {
    if (!window.confirm(`${s.name} adlı öğrenci ve tüm verileri silinsin mi?`)) return;
    try { await api.delete(`/users/${s.id}`); toast.success("Öğrenci silindi"); setStudents((prev) => prev.filter((x) => x.id !== s.id)); }
    catch { toast.error("Silme başarısız"); }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-600">
          <UsersRound className="h-6 w-6" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">{isAdmin ? "Öğrenci Sayfaları (Canlı Görünüm)" : "Öğrencilerim"}</h1>
          <p className="text-slate-500">{isAdmin ? "Öğrenci seçerek öğrencinin kendi ekranında gördüğü panelleri canlı görüntüle." : "Öğrenci seçerek haftalık planını görüntüle."}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            data-testid="coach-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Öğrenci ara…"
            className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
          />
        </div>
        <select
          data-testid="coach-grade-filter"
          value={grade}
          onChange={(e) => setGrade(e.target.value)}
          className="rounded-2xl border border-slate-200 bg-white px-4 py-3 font-semibold text-slate-700 outline-none focus:border-indigo-400"
        >
          <option value="">Tüm Sınıflar</option>
          {GRADE_GROUPS.map((grp) => (
            <optgroup key={grp.label} label={grp.label}>
              {grp.grades.map((g) => <option key={g} value={g}>{g}. Sınıf</option>)}
            </optgroup>
          ))}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">
          Öğrenci bulunamadı.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((s) => {
            const q = `?student=${s.id}&name=${encodeURIComponent(s.name)}`;
            const links = [
              { to: `/genel-bakis${q}`, label: "Genel", icon: LayoutDashboard },
              { to: `/calisma-programi${q}`, label: "Program", icon: LayoutGrid },
              { to: `/program${q}`, label: "Haftalık", icon: CalendarDays },
              { to: `/aylik-degerlendirme${q}`, label: "Aylık", icon: CalendarCheck2 },
              { to: `/okuma-kitaplari${q}`, label: "Kitap", icon: Library },
              { to: `/gunluk-soru${q}`, label: "Günlük", icon: PencilLine },
              { to: `/ozet-ezber${q}`, label: "Özet", icon: BookText },
              { to: `/kaynak-kutuphanesi${q}`, label: "Kaynak", icon: FolderOpen },
              { to: `/deneme-karti${q}`, label: "Kart", icon: FileText },
              { to: `/optik-sinav${q}`, label: "Optik", icon: ScanLine },
              { to: `/optik-sinav${q}`, label: "Karne", icon: FileCheck2 },
              { to: `/denemeler${q}`, label: "Denemeler", icon: Trophy },
              ...(EXAM_TRACK_GRADES.includes(s.grade) ? [{ to: `/sinav-takip${q}`, label: "Sınav", icon: ClipboardCheck }] : []),
            ];
            return (
              <div key={s.id} data-testid={`coach-student-${s.id}`} className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm transition-all hover:shadow-md">
                <div className="flex items-center gap-4">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-xl text-white ${GRADE_COLORS[(s.grade || 1) - 1] || "bg-slate-400"}`}>
                    <GraduationCap className="h-6 w-6" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-bold text-slate-900">{s.name}</div>
                    <div className="text-sm text-slate-500">{s.grade ? `${s.grade}. Sınıf` : "—"}{s.section ? ` / ${s.section}` : ""}</div>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1">
                      <button data-testid={`coach-edit-${s.id}`} title="Düzenle" onClick={() => navigate(`/admin/kullanicilar?edit=${s.username}`)} className="rounded-lg p-1.5 text-slate-400 transition hover:bg-indigo-50 hover:text-indigo-600 active:scale-90"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`coach-del-${s.id}`} title="Sil" onClick={() => delStudent(s)} className="rounded-lg p-1.5 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600 active:scale-90"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  )}
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {links.map((l) => {
                    const Icon = l.icon;
                    return (
                      <button key={l.to} data-testid={`coach-open-${l.label.toLowerCase()}-${s.id}`} onClick={() => navigate(l.to)} className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-2 py-2 text-xs font-semibold text-slate-600 transition hover:border-indigo-400 hover:text-indigo-600 active:scale-95">
                        <Icon className="h-3.5 w-3.5" /> {l.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
